import {
  ChoiceGroup,
  Separator,
  Stack,
  Text,
  PrimaryButton,
  DefaultButton
} from "@fluentui/react";
import { useCallback, useEffect, useState } from "react";

import {
  SignatureSelectorContentProps,
  SignatureOption,
  AccountSignatureContact,
  SignatureUpdateData
} from "./interfaces";
import { RenderDynamicContactOptions } from "./RenderDynamicContactOptions";
import { RenderFixedContactOptions } from "./RenderFixedContactOptions";

import { mapContactType } from "@/components/Contracts/ConfirmationStep/Signature/contractType.mapper";
import {
  AccountContactType,
  SelectedSignatureProps
} from "@/components/Contracts/ConfirmationStep/Signature/signature.interface";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { AccountType } from "@/modules/Accounts/types";
import { SignatureType } from "@/modules/Contracts/enums";
import { BusinessUnitsSignatures } from "@/modules/Settings/types/BusinessUnits.types";

export function SignatureSelectorContent({
  account,
  onConfirm,
  businessUnitId,
  termDataDraft,
  onCancel
}: SignatureSelectorContentProps) {
  const [signatureOptions, setSignatureOptions] = useState<SignatureOption[]>(
    []
  );
  const [selectedSignatureProps, setSelectedSignatureProps] =
    useState<SelectedSignatureProps>(null);
  const [contacts, setContacts] = useState<AccountSignatureContact[]>([]);
  const [selectedContact, setSelectedContact] =
    useState<AccountSignatureContact>(null);

  const { execute } = useApi();

  const fetchOptions = useCallback(async () => {
    const response = await execute<BusinessUnitsSignatures[]>({
      url: `business-units/${businessUnitId}`
    });

    const mappedOptions: SignatureOption[] = response.data.map(
      (value: BusinessUnitsSignatures) => ({
        key: value.signature.type,
        text: value.signature.name,
        id: value.signature.id.toString()
      })
    );

    setSignatureOptions(mappedOptions);
  }, [execute, businessUnitId]);

  const getAccountSignatureContacts = useCallback(
    async (contactType: AccountContactType): Promise<void> => {
      const result = await execute<AccountSignatureContact[]>({
        url: `accounts/signature-contacts/${account.id}`,
        params: {
          contactType
        }
      });

      setContacts(result.data);

      if (
        account.accountType === AccountType.PHYSICAL &&
        result.data.length > 0
      ) {
        setSelectedContact(result.data[0]);
      }

      if (account.accountType === AccountType.LEGAL) {
        setSelectedContact(null);
      }
    },
    [execute, account]
  );

  const getTermDataDraftContacts = useCallback(
    (signatureType: SignatureType): void => {
      const contact =
        signatureType === SignatureType.ELETRONIC_WHATSAPP
          ? termDataDraft.whatsapp
          : termDataDraft.email;

      setContacts([
        {
          contact: contact,
          fullName: termDataDraft.fullName
        }
      ]);
    },
    [termDataDraft]
  );

  const handleChangeSignaturesCallback = useCallback(
    (_: React.InputHTMLAttributes<HTMLElement>, option: SignatureOption) => {
      const newSignatureProps: SelectedSignatureProps = {
        id: option.id,
        name: mapContactType(option.key)
      };

      setSelectedSignatureProps(prevProps => {
        if (termDataDraft) {
          getTermDataDraftContacts(option.key);
        }

        if (prevProps?.name !== AccountContactType.STANDARD && account) {
          void getAccountSignatureContacts(newSignatureProps?.name);
        }

        return newSignatureProps;
      });

      setSelectedContact(null);
    },
    [
      account,
      termDataDraft,
      getAccountSignatureContacts,
      getTermDataDraftContacts
    ]
  );

  const isSaveEnabled = () => {
    if (!selectedSignatureProps) return false;

    if (account?.accountType === AccountType.PHYSICAL) {
      return Boolean(selectedContact);
    }

    if (account?.accountType === AccountType.LEGAL || termDataDraft) {
      return contacts.length > 0;
    }

    return false;
  };

  const resetModalState = useCallback(() => {
    setSelectedSignatureProps(null);
    setSelectedContact(null);
    setContacts([]);
  }, []);

  const handleCancel = useCallback(() => {
    resetModalState();
    onCancel();
  }, [resetModalState, onCancel]);

  const handleSave = useCallback(async () => {
    if (!selectedSignatureProps) return;

    const signatureData: SignatureUpdateData = {
      signatureId: selectedSignatureProps.id
    };

    if (account?.accountType === AccountType.PHYSICAL && selectedContact?.id) {
      signatureData.accountSignContactId = selectedContact.id;
    }

    const successfullyUpdatedSignatureMethod = await onConfirm(signatureData);

    if (successfullyUpdatedSignatureMethod) {
      handleCancel();
    }
  }, [
    selectedSignatureProps,
    account?.accountType,
    selectedContact,
    onConfirm,
    handleCancel
  ]);

  useEffect(() => {
    void fetchOptions();
  }, [fetchOptions]);

  const renderSignatureOptions = () => {
    if (signatureOptions?.length > 0) {
      return (
        <ChoiceGroup
          options={signatureOptions}
          radioGroup="none"
          multiple={false}
          onChange={handleChangeSignaturesCallback}
        />
      );
    }

    return (
      <Stack horizontalAlign="center" tokens={{ padding: 20 }}>
        <Text
          variant="medium"
          styles={{ root: { fontWeight: 600, textAlign: "center" } }}
        >
          Nenhuma opção de assinatura encontrada, por favor, contate o suporte!
        </Text>
      </Stack>
    );
  };

  const renderContactOptions = () => {
    const isTermDataDraft = Boolean(termDataDraft);
    const isLegalAccount = account?.accountType === AccountType.LEGAL;
    const shouldRenderFixedContacts = isLegalAccount || isTermDataDraft;

    const isPhysicalAccount = account?.accountType === AccountType.PHYSICAL;
    const shouldRenderDynamicContactOptions = isPhysicalAccount;

    return (
      <Stack horizontalAlign="center" tokens={{ childrenGap: 20 }}>
        {shouldRenderFixedContacts && (
          <RenderFixedContactOptions
            contacts={contacts}
            contactType={selectedSignatureProps?.name}
          />
        )}
        {shouldRenderDynamicContactOptions && (
          <RenderDynamicContactOptions
            contacts={contacts}
            contactType={selectedSignatureProps?.name}
            selectedContact={selectedContact}
            setSelectedContact={setSelectedContact}
          />
        )}
      </Stack>
    );
  };

  return (
    <>
      {renderSignatureOptions()}
      <Separator />
      {selectedSignatureProps && renderContactOptions()}

      <Stack
        horizontal
        horizontalAlign="end"
        tokens={{ childrenGap: 8 }}
        styles={{ root: { marginTop: 16 } }}
      >
        <PrimaryButton
          onClick={() => void handleSave()}
          text="Salvar e reenviar"
          disabled={!isSaveEnabled()}
        />
        <DefaultButton onClick={handleCancel} text="Cancelar" />
      </Stack>
    </>
  );
}
